import { FormValues } from '@ahmdigital/form-validation/lib/validators/types';
import { PRODUCT_TYPE, SCALE } from '@ahmdigital/constants';
import { toString } from 'lodash/fp';

import { FIELD_NAMES, FIND_COVER_BUTTON_TEXT } from './constants';
import { getFieldWidth } from '../utils/get-field-truncation';
import { INCOME_TIER_NONE } from '../../../components/pricing-drawer/constants';
import { WidgetConfig } from '../../../components/embedded-widget/types';
import addNoRebateIncomeTier from '../../../utils/add-no-rebate-income-tier';
import AgeSelector from '../components/health-widget/modal-components/age-selector';
import AnnualIncomeSelector from '../components/health-widget/modal-components/annual-income-selector';
import constants from '../../../ahm-constants';
import CoverSelector from '../components/health-widget/modal-components/cover-selector';
import getIncomeTierOptions, { IncomeType } from '../../../utils/get-income-tier-options';
import ScaleSelector from '../components/health-widget/modal-components/scale-selector';
import StateSelector from '../components/health-widget/modal-components/state-selector';
import type { Breakpoint } from '../../../utils/device';
import type { HealthFormValues, HeroSectionProps } from '../types';

export const diversifiedHeroData: HeroSectionProps = {
  urlObject: {
    name: 'Manage your health cover',
    url: constants.URLS.MEMBERS.HOME,
  },
};

export const heroSectionData: HeroSectionProps = {
  urlObject: {
    name: FIND_COVER_BUTTON_TEXT,
    url: constants.URLS.SALES.REFINE_PRICE,
  },
};

const hasScaleLabel = (scale: string): scale is keyof typeof constants.SCALE_LABEL => scale in constants.SCALE_LABEL;

const getFormattedScaleLabel = (scale: string | undefined) => {
  if (!scale) return '';
  return hasScaleLabel(scale) ? constants.SCALE_LABEL[scale] : '';
};

type DisplayLabelsArgs = {
  allOptions: Array<{ text?: string }>;
  customerFormInitialValues: HealthFormValues;
  incomeTierNum: number;
  values: HealthFormValues;
};

const getDisplayLabels = ({ allOptions, customerFormInitialValues, incomeTierNum, values }: DisplayLabelsArgs) => {
  const scaleLabel = getFormattedScaleLabel(values.scale ?? customerFormInitialValues.scale);

  const stateLabel = constants.STATE_LABEL[values.state ?? customerFormInitialValues.state];

  const incomeTierLabel = allOptions[incomeTierNum]?.text ?? '';

  const typeDisplay = values.productType ?? customerFormInitialValues.productType ?? PRODUCT_TYPE.EXTRAS;
  const uppercaseTypeDisplay = typeDisplay.toUpperCase() as keyof typeof constants.TYPE_LABEL;
  const coverLabel = constants.TYPE_LABEL[uppercaseTypeDisplay];

  return {
    coverLabel,
    incomeTierLabel,
    scaleLabel,
    stateLabel,
  };
};

export const getHealthWidgetData = (
  widgetValues: FormValues,
  customerFormInitialValues: HealthFormValues,
  breakpoint: Breakpoint,
): WidgetConfig => {
  const values = widgetValues as HealthFormValues;
  const incomeType = values.incomeType ?? customerFormInitialValues.incomeType;
  const currentRebate = values.rebate ?? customerFormInitialValues.rebate;
  const incomeTier =
    currentRebate === false ? INCOME_TIER_NONE : values.incomeTier ?? customerFormInitialValues.incomeTier;

  const incomeTierOptions = getIncomeTierOptions({ incomeType: incomeType as IncomeType });
  const allOptions = addNoRebateIncomeTier(incomeTierOptions);
  // If the income tier is INCOME_TIER_NONE, then preselect the income tier none index which is last one
  const INCOME_TIER_INDEX = allOptions.length - 1;
  const incomeTierNum = incomeTier === INCOME_TIER_NONE ? INCOME_TIER_INDEX : Number(incomeTier);

  const showSingleAge = values.scale === SCALE.SINGLE || values.scale === SCALE.SINGLE_PARENT;
  const showPartnersAge = values?.partnerAge && !showSingleAge;

  const getAgePlaceholder = () => {
    if (!values?.age) return 'e.g. 30';
    if (!showPartnersAge) return toString(values.age);
    return `${values.age}, ${values.partnerAge}`;
  };

  const formattedAgeLabel = getAgePlaceholder();
  const { coverLabel, incomeTierLabel, scaleLabel, stateLabel } = getDisplayLabels({
    allOptions,
    customerFormInitialValues,
    incomeTierNum,
    values,
  });

  return {
    buttonSpacing: { lg: 2.5, md: 1.8 },
    fields: [
      {
        displayValue: {
          getValue: () => coverLabel,
          hasValue: Boolean(values.productType),
        },
        fieldWidth: getFieldWidth('coverType', breakpoint, 11.375),
        modalContentComponent: CoverSelector,
        name: FIELD_NAMES.COVER_TYPE,
      },
      {
        displayValue: {
          getValue: () => scaleLabel,
          hasValue: Boolean(values.scale),
        },
        fieldWidth: getFieldWidth('for', breakpoint, 11.625),
        modalContentComponent: ScaleSelector,
        name: FIELD_NAMES.FOR,
      },
      {
        displayValue: {
          getValue: () => stateLabel,
          hasValue: Boolean(values.state),
        },
        fieldWidth: getFieldWidth('livingIn', breakpoint, 10),
        modalContentComponent: StateSelector,
        name: FIELD_NAMES.LIVING_IN,
      },
      {
        displayValue: {
          customLabel: 'Age',
          getValue: () => formattedAgeLabel,
          hasValue: Boolean(values.age),
        },
        fieldWidth: getFieldWidth('ageSection', breakpoint, 9),
        modalContentComponent: AgeSelector,
        name: FIELD_NAMES.AGE_SECTION,
      },
      {
        displayValue: {
          getValue: () => incomeTierLabel,
          hasValue: Boolean(values.incomeTier),
        },
        fieldWidth: getFieldWidth('annualIncome', breakpoint, 15.625),
        modalContentComponent: AnnualIncomeSelector,
        name: FIELD_NAMES.ANNUAL_INCOME,
      },
    ],
  };
};
